import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { formatCurrency, formatDate } from '@/helpers/billing'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux'
import {
  BillingSubscription,
  CreateOneTimeFeeAndProcessResponse,
  OneTimeFee,
  PlaybookQuotaInfoResponse
} from '@/types/billing'
import { showNotification } from '@/services/notificationService'
import { TCSAPIResponse } from '@/types/types'
import {
  createOneTimeFee,
  grantOrRevokeFreePlaybookQuota,
  getSubscription,
  waiveOneTimeFee,
  voidOneTimeFee
} from '@/services/billingService'
import { BillingAPI } from '@/API/Billing'
import { Check, ExternalLink, UserPlus, Gift, Ban, FileCheck } from 'lucide-react'

const PRICING_MODEL_LABEL: Record<string, string> = {
  plan_tier_based_v1: 'Legacy (tier-based)',
  playbook_based_v2: 'Playbook-based'
}

/** Fee statuses that allow void/waive actions. */
const VOIDABLE_FEE_STATUSES: string[] = ['pending', 'checkout_open', 'failed']

export const AdminBillingTools = ({
  token,
  organizationId,
  onUpdate
}: {
  token: string
  organizationId: string
  onUpdate: () => void
}) => {
  const dispatch: AppDispatch = useDispatch()
  const { loading: appLoading } = useSelector((state: RootState) => state.app)
  const { userData } = useSelector((state: RootState) => state.user)

  const [subscription, setSubscription] = useState<BillingSubscription | null>(null)
  const [quotaInfo, setQuotaInfo] = useState<PlaybookQuotaInfoResponse | null>(null)
  const [oneTimeFees, setOneTimeFees] = useState<OneTimeFee[]>([])
  const [loadingOverview, setLoadingOverview] = useState(true)
  const [loadingFees, setLoadingFees] = useState(false)
  const [ensureLoading, setEnsureLoading] = useState(false)
  const [portalLoading, setPortalLoading] = useState(false)
  const [voidWaiveLoadingId, setVoidWaiveLoadingId] = useState<string | null>(null)

  const [oneTimeFeeAmount, setOneTimeFeeAmount] = useState(500)
  const [oneTimeFeeLabel, setOneTimeFeeLabel] = useState('Setup Fee')
  const [oneTimeFeeNotes, setOneTimeFeeNotes] = useState('')
  const [createdFee, setCreatedFee] = useState<CreateOneTimeFeeAndProcessResponse | null>(null)

  const [freeQuotaAmount, setFreeQuotaAmount] = useState(0)
  const [freeQuotaNotes, setFreeQuotaNotes] = useState('')
  const [freeQuotaLoading, setFreeQuotaLoading] = useState(false)

  const refreshOverview = useCallback(async () => {
    if (!token || !organizationId) return
    setLoadingOverview(true)
    const [subResp, quotaResp] = await Promise.all([
      dispatch(getSubscription(token, organizationId)),
      BillingAPI.getPlaybookQuotaInfo(token, organizationId)
    ])
    if (subResp.data) setSubscription(subResp.data)
    if (quotaResp.data) setQuotaInfo(quotaResp.data)
    setLoadingOverview(false)
  }, [dispatch, token, organizationId])

  const refreshFees = useCallback(async () => {
    if (!token || !organizationId) return
    setLoadingFees(true)
    const resp = await BillingAPI.getOneTimeFees(token, organizationId)
    if (resp.data?.fees) setOneTimeFees(resp.data.fees)
    setLoadingFees(false)
  }, [token, organizationId])

  useEffect(() => {
    refreshOverview()
  }, [refreshOverview])

  useEffect(() => {
    refreshFees()
  }, [refreshFees])

  if (!userData?.isTCSAdmin) {
    return null
  }

  const handleEnsureCustomer = async () => {
    if (!organizationId) return
    setEnsureLoading(true)
    const resp = await BillingAPI.ensureStripeCustomer(token, organizationId)
    setEnsureLoading(false)
    if (resp.data) {
      showNotification({ data: resp.data as unknown as TCSAPIResponse, err: undefined }, 'Billing account ready', {})
      refreshOverview()
    } else {
      showNotification({ data: undefined, err: resp.err }, '', {})
    }
  }

  const handlePortalLink = async () => {
    if (!organizationId) return
    setPortalLoading(true)
    const resp = await BillingAPI.generatePortalLink(token, organizationId)
    setPortalLoading(false)
    if (resp.data?.url) {
      window.open(resp.data.url, '_blank', 'noopener,noreferrer')
    } else {
      showNotification({ data: undefined, err: resp.err }, '', {})
    }
  }

  const handleCreateOneTimeFee = async () => {
    setCreatedFee(null)
    const response = await dispatch(
      createOneTimeFee(token, organizationId, oneTimeFeeAmount * 100, oneTimeFeeLabel, oneTimeFeeNotes || undefined)
    )
    if (response.data) {
      setCreatedFee(response.data)
      onUpdate()
      refreshFees()
    } else {
      showNotification({ data: undefined, err: response.err }, '', {})
    }
  }

  const handleResetCreateFee = () => {
    setOneTimeFeeAmount(500)
    setOneTimeFeeLabel('Setup Fee')
    setOneTimeFeeNotes('')
    setCreatedFee(null)
  }

  const handleGrantRevokeQuota = async () => {
    if (freeQuotaAmount === 0) return
    setFreeQuotaLoading(true)
    const resp = await dispatch(
      grantOrRevokeFreePlaybookQuota(token, organizationId, freeQuotaAmount, freeQuotaNotes || undefined)
    )
    setFreeQuotaLoading(false)
    if (resp.data) {
      showNotification({ data: resp.data as unknown as TCSAPIResponse, err: undefined }, 'Free playbook quota updated', {})
      setFreeQuotaAmount(0)
      setFreeQuotaNotes('')
      refreshOverview()
    }
  }

  const handleVoid = async (feeId: string) => {
    setVoidWaiveLoadingId(feeId)
    const resp = await dispatch(voidOneTimeFee(token, feeId))
    setVoidWaiveLoadingId(null)
    if (resp.data) {
      showNotification({ data: resp.data as unknown as TCSAPIResponse, err: undefined }, 'Fee voided', {})
      refreshFees()
    }
  }

  const handleWaive = async (feeId: string) => {
    setVoidWaiveLoadingId(feeId)
    const resp = await dispatch(waiveOneTimeFee(token, feeId))
    setVoidWaiveLoadingId(null)
    if (resp.data) {
      showNotification({ data: resp.data as unknown as TCSAPIResponse, err: undefined }, 'Fee waived', {})
      refreshFees()
    }
  }

  const isPlaybookBased = subscription?.pricingModel === 'playbook_based_v2'
  /** Show grant UI for playbook-based subscriptions or orgs with no subscription. N/A only for legacy plans. */
  const canGrantFreeQuota = isPlaybookBased || !subscription

  return (
    <div className="space-y-8">
      <div>
        <h4 className="mb-4 text-lg font-semibold text-gray-900">Admin Billing Tools</h4>
      </div>

      {/* Overview */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h5 className="mb-4 font-medium text-gray-900">Overview</h5>
        {loadingOverview ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="space-y-4">
            {subscription ? (
              <>
                <div className="grid gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-600">Pricing model:</span>
                    <Badge variant="outline">
                      {PRICING_MODEL_LABEL[subscription.pricingModel ?? ''] ?? subscription.pricingModel ?? '—'}
                    </Badge>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Status:</span>{' '}
                    <span className="text-gray-900">{subscription.status}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Period end:</span>{' '}
                    <span className="text-gray-900">
                      {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                {isPlaybookBased && quotaInfo && (
                  <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <p className="text-sm font-medium text-gray-700">Playbook quota</p>
                    <p className="mt-1 text-sm text-gray-600">
                      Effective (current quota): {quotaInfo.effectiveSubscribedPlaybookCount ?? quotaInfo.subscribedPlaybookCount ?? '—'} · Target (after period end): {quotaInfo.targetSubscribedPlaybookCount ?? quotaInfo.subscribedPlaybookCount ?? '—'} · Active: {quotaInfo.activePlaybookCount} · Available: {quotaInfo.availableQuota}
                      {typeof quotaInfo.freePlaybookCount === 'number' && (
                        <> · Free: {quotaInfo.freePlaybookCount}</>
                      )}
                      {' · Total quota: '}
                      {quotaInfo.totalQuota ?? '—'}
                    </p>
                    {quotaInfo.hasPendingDecrease && quotaInfo.targetSubscribedPlaybookCount != null && (
                      <p className="mt-2 text-sm text-blue-700">
                        Quota will drop to {quotaInfo.targetSubscribedPlaybookCount} on{' '}
                        {quotaInfo.currentPeriodEnd
                          ? new Date(quotaInfo.currentPeriodEnd).toLocaleDateString()
                          : '—'}
                        .
                      </p>
                    )}
                    {subscription.hasPendingIncrease && (subscription.pendingQuota ?? 0) > 0 && (
                      <p className="mt-2 text-sm text-yellow-700">
                        Payment pending for {subscription.pendingQuota} playbook{subscription.pendingQuota !== 1 ? 's' : ''}. Use &quot;Open in Stripe Portal&quot; below to help the org pay.
                      </p>
                    )}
                  </div>
                )}
                {!isPlaybookBased && (
                  <p className="text-sm text-gray-500">Playbook quota: N/A (legacy plan)</p>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500">No subscription for this organization.</p>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h5 className="mb-4 font-medium text-gray-900">Actions</h5>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleEnsureCustomer}
            disabled={ensureLoading || appLoading}
          >
            <UserPlus className="mr-2 h-4 w-4" />
            {ensureLoading ? '…' : 'Set up billing account'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePortalLink}
            disabled={portalLoading || appLoading}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            {portalLoading ? '…' : 'Open in Stripe Portal'}
          </Button>
        </div>
      </div>

      {/* Free playbook quota */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h5 className="mb-4 font-medium text-gray-900">Free playbook quota</h5>
        {canGrantFreeQuota ? (
          <div className="space-y-3">
            {quotaInfo && (
              <p className="text-sm text-gray-600">
                Active: {quotaInfo.activePlaybookCount} · Available: {quotaInfo.availableQuota}
                {typeof quotaInfo.freePlaybookCount === 'number' && (
                  <> · Free: {quotaInfo.freePlaybookCount}</>
                )}
              </p>
            )}
            <div>
              <Label htmlFor="admin-quota-amount" className="text-gray-700">
                Amount (+ grant, − revoke)
              </Label>
              <Input
                id="admin-quota-amount"
                type="number"
                value={freeQuotaAmount === 0 ? '' : freeQuotaAmount}
                onChange={(e) => setFreeQuotaAmount(Number(e.target.value) || 0)}
                placeholder="0"
                className="mt-1 max-w-[120px] bg-white text-gray-900"
              />
            </div>
            <div>
              <Label htmlFor="admin-quota-notes" className="text-gray-700">
                Notes (optional)
              </Label>
              <Input
                id="admin-quota-notes"
                value={freeQuotaNotes}
                onChange={(e) => setFreeQuotaNotes(e.target.value)}
                placeholder="e.g. Trial"
                className="mt-1 bg-white text-gray-900"
              />
            </div>
            <Button
              onClick={handleGrantRevokeQuota}
              disabled={freeQuotaAmount === 0 || freeQuotaLoading}
              size="sm"
            >
              <Gift className="mr-2 h-4 w-4" />
              {freeQuotaLoading ? '…' : 'Grant / Revoke'}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-gray-500">N/A for legacy plans.</p>
        )}
      </div>

      {/* One-time fees: list + create + void/waive */}
      <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-6">
        <h5 className="mb-4 font-medium text-amber-900">One-time fees</h5>

        <div className="mb-6 space-y-4">
          <h6 className="text-sm font-medium text-amber-800">Create</h6>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="fee-label" className="text-gray-700">
                Label
              </Label>
              <Input
                id="fee-label"
                value={oneTimeFeeLabel}
                onChange={(e) => setOneTimeFeeLabel(e.target.value)}
                placeholder="Setup Fee, Consultation…"
                className="mt-1 bg-white text-gray-900"
                disabled={!!createdFee}
              />
            </div>
            <div>
              <Label htmlFor="fee-amount" className="text-gray-700">
                Amount (USD)
              </Label>
              <Input
                id="fee-amount"
                type="number"
                min={1}
                max={50000}
                value={oneTimeFeeAmount}
                onChange={(e) => setOneTimeFeeAmount(Number(e.target.value))}
                className="mt-1 bg-white text-gray-900"
                disabled={!!createdFee}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="fee-notes" className="text-gray-700">
              Notes (optional)
            </Label>
            <Input
              id="fee-notes"
              value={oneTimeFeeNotes}
              onChange={(e) => setOneTimeFeeNotes(e.target.value)}
              className="mt-1 bg-white text-gray-900"
              disabled={!!createdFee}
            />
          </div>
          {!createdFee ? (
            <Button
              onClick={handleCreateOneTimeFee}
              disabled={appLoading || !oneTimeFeeLabel || oneTimeFeeAmount < 1}
              variant="destructive"
              size="sm"
            >
              Create one-time fee
            </Button>
          ) : (
            <div className="space-y-2">
              <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                <Check className="mr-2 inline h-4 w-4" /> Fee created. Status: {createdFee.fee?.status}
                {createdFee.checkoutUrl && (
                  <a
                    href={createdFee.checkoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 text-xs underline"
                  >
                    Checkout link
                  </a>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={handleResetCreateFee}>
                Create another
              </Button>
            </div>
          )}
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <h6 className="text-sm font-medium text-amber-800">List</h6>
            <Button variant="ghost" size="sm" onClick={refreshFees} disabled={loadingFees}>
              Refresh
            </Button>
          </div>
          {loadingFees ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : oneTimeFees.length === 0 ? (
            <p className="text-sm text-gray-500">No one-time fees.</p>
          ) : (
            <ul className="space-y-3">
              {oneTimeFees.map((fee) => {
                const canVoidWaive = VOIDABLE_FEE_STATUSES.includes(fee.status)
                const loadingThis = voidWaiveLoadingId === fee.id
                return (
                  <li
                    key={fee.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-white p-3"
                  >
                    <div>
                      <span className="font-medium text-gray-900">{fee.label}</span>
                      <span className="ml-2 text-sm text-gray-600">
                        {formatCurrency(fee.amountCents / 100)} · {fee.status}
                      </span>
                      {fee.dateCreated && (
                        <span className="ml-2 text-xs text-gray-500">{formatDate(fee.dateCreated)}</span>
                      )}
                    </div>
                    {canVoidWaive && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVoid(fee.id)}
                          disabled={loadingThis}
                        >
                          <Ban className="mr-1 h-3 w-3" />
                          Void
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleWaive(fee.id)}
                          disabled={loadingThis}
                        >
                          <FileCheck className="mr-1 h-3 w-3" />
                          Waive
                        </Button>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <p className="mt-4 text-xs text-amber-800">
          Admin actions affect this organization’s billing. Use with caution.
        </p>
      </div>
    </div>
  )
}
